import { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Subtle diaspora celebration — 60 particles falling from top of viewport.
 * Gold, ivory, and Liberia flag reds/blues in a hierarchy that suits the
 * RideXelite palette. Renders inside a fixed pointer-events-none container.
 *
 * Consumer controls the "on/off" lifetime by conditionally rendering it;
 * particles auto-fade after ~4s via CSS-driven Framer Motion transitions.
 */

const PALETTE = [
  { color: "#D4AF37", weight: 5 }, // gold — dominant
  { color: "#F3E5AB", weight: 3 }, // pale gold
  { color: "#FFFFFF", weight: 3 }, // ivory
  { color: "#BF0A30", weight: 1 }, // Liberia flag red
  { color: "#002868", weight: 1 }, // Liberia flag blue
];

// Weighted color pick
function pickColor() {
  const total = PALETTE.reduce((s, p) => s + p.weight, 0);
  let r = Math.random() * total;
  for (const p of PALETTE) {
    r -= p.weight;
    if (r <= 0) return p.color;
  }
  return PALETTE[0].color;
}

export const GoldConfetti = ({ count = 60, duration = 4 }) => {
  const particles = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100, // vw
        delay: Math.random() * 0.6,
        fall: 2.4 + Math.random() * 2.2, // seconds
        drift: (Math.random() - 0.5) * 220, // px horizontal drift
        rotate: (Math.random() - 0.5) * 720,
        size: 6 + Math.random() * 8,
        color: pickColor(),
        shape: Math.random() > 0.5 ? "rect" : "circle",
      })),
    [count]
  );

  return (
    <div
      data-testid="gold-confetti"
      className="fixed inset-0 z-[60] pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: -20, opacity: 0, rotate: 0 }}
          animate={{
            x: p.drift,
            y: typeof window !== "undefined" ? window.innerHeight + 40 : 900,
            opacity: [0, 1, 1, 0],
            rotate: p.rotate,
          }}
          transition={{
            duration: p.fall,
            delay: p.delay,
            ease: "easeIn",
            opacity: { times: [0, 0.08, 0.85, 1], duration: p.fall, delay: p.delay },
          }}
          style={{
            left: `${p.left}vw`,
            top: "-24px",
            width: p.size,
            height: p.shape === "circle" ? p.size : p.size * 0.5,
            background: p.color,
            borderRadius: p.shape === "circle" ? "9999px" : "2px",
            boxShadow: `0 0 8px ${p.color}55`,
            position: "absolute",
            display: "inline-block",
          }}
        />
      ))}
      {/* subtle gold flash to sell the moment */}
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 40% at 50% 30%, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.05) 45%, transparent 75%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: Math.min(duration, 1.6), ease: "easeOut" }}
      />
    </div>
  );
};

export default GoldConfetti;
